import { StackFrame, parse } from 'stacktrace-parser';
import {
  createEventEnvelope,
  DsnComponents,
  Event,
  SdkMetadata,
} from '@sentry/core';
import uuid4 from '../utils/uuid';

function formatStackFrame({
  file,
  methodName,
  lineNumber,
  column,
}: StackFrame) {
  // documented here https://develop.sentry.dev/sdk/data-model/event-payloads/stacktrace/
  return {
    filename: file,
    function: methodName,
    lineno: lineNumber,
    colno: column,
    in_app: true,
  };
}
const parseException = (exception: Error) => {
  if (!exception.stack) {
    return {
      type: exception.name || 'Error',
      value: exception.message,
    };
  }

  return {
    type: exception.name || 'Error',
    value: exception.message,
    stacktrace: {
      frames: parse(exception.stack).map(formatStackFrame),
    },
  };
};

type SentryClientConfig = {
  dsn: string;
  version: string;
  client: string;
  release: string;
  env: string;
};

type SentryEventAttributes = Pick<
  Event,
  'tags' | 'extra' | 'fingerprint' | 'level'
>;

class SentryService {
  /** URL to send events to */
  private sentryUrl: string;

  /** Headers to send with the event */
  private headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  /** Sentry release */
  private release: string;

  /** staging or production */
  private env: string;

  private dsn: DsnComponents;

  private meta: SdkMetadata;

  constructor(config: SentryClientConfig) {
    const match = config.dsn.match(/^https:\/\/([^@]+)@([^/]+)\/(\d+)/);
    if (!match) {
      // throw new Error('Invalid Sentry DSN format');
      // fail silently
      return;
    }

    const [, publicKey, host, projectId] = match;
    const urlParams = new URLSearchParams({
      sentry_version: config.version,
      sentry_client: config.client,
      sentry_key: publicKey,
    });

    this.sentryUrl = `https://${host}/api/${projectId}/envelope/?${urlParams}`;
    this.release = config.release;
    this.env = config.env;

    this.dsn = {
      protocol: 'https',
      host,
      projectId,
      publicKey,
    };

    this.meta = {
      sdk: {
        name: 'custom-client',
        version: '0.0.2',
      },
    };
  }

  async log(exception: Error, attributes: SentryEventAttributes) {
    if (!SENTRY_LOGS_ENABLED) return;
    try {
      // documented here https://develop.sentry.dev/sdk/data-model/event-payloads/
      const event: Event = {
        event_id: uuid4(),
        timestamp: Date.now() / 1000,
        platform: 'javascript',
        logger: 'custom',
        release: this.release,
        environment: this.env,
      };

      const exceptionData = parseException(exception);

      const envelope = createEventEnvelope(
        {
          ...event,
          ...attributes,
          exception: {
            values: [exceptionData],
          },
        },
        this.dsn,
        this.meta,
      );
      const encodedBody = envelope
        .flat(2)
        .map((item) => JSON.stringify(item))
        .join('\n');
      await fetch(this.sentryUrl, {
        method: 'POST',
        headers: this.headers,
        body: encodedBody,
      });
    } catch (e) {
      // fail silently
    }
  }
}

const sentryClient = new SentryService({
  dsn: SENTRY_DSN,
  version: '7',
  client: `custom-client@0.0.2`,
  release: SENTRY_RELEASE,
  env: ENV,
});

interface ErrorLoggerInterface {
  gateway: string;
  sdk: string;
  init(gateway: string): void;
  log(exception: Error): void;
}

function ErrorLogger(this: ErrorLoggerInterface, sdk: string) {
  this.gateway = '';
  this.sdk = sdk;
}
ErrorLogger.prototype.init = function init(
  this: ErrorLoggerInterface,
  gateway: string,
) {
  this.gateway = gateway;
};
ErrorLogger.prototype.log = function log(
  this: ErrorLoggerInterface,
  exception: Error,
) {
  sentryClient.log(exception, {
    tags: {
      gateway: this.gateway,
      sdk: this.sdk,
    },
  });
};
export const scErrorLogger: ErrorLoggerInterface = new ErrorLogger('scDK');
export const loanErrorLogger: ErrorLoggerInterface = new ErrorLogger('ScLoan');
